import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FeatureJobApplications } from '../models/featurejobapplications.model';
import {
  DashboardActivity,
  DashboardPipelineStage,
} from '../models/dashboard.model';
import { Interview } from '../../job-application/models/interview.model';
import { formatStageLabel } from '../../job-application/utils/pipeline-board.utils';

dayjs.extend(utc);

const CANCELLED_INTERVIEW_STATUSES = new Set(['CANCELLED', 'NO_SHOW']);

export function getDashboardWeekRange(reference: Dayjs): {
  from: string;
  to: string;
} {
  return {
    from: reference
      .startOf('week')
      .startOf('day')
      .utc()
      .format('YYYY-MM-DDTHH:mm:ss'),
    to: reference
      .endOf('week')
      .endOf('day')
      .utc()
      .format('YYYY-MM-DDTHH:mm:ss'),
  };
}

export function getDashboardGreeting(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export function getPersonInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export function formatDashboardDate(
  value: string,
  reference: Dayjs = dayjs()
): string {
  if (!value || !dayjs(value).isValid()) return 'Date unavailable';

  const date = dayjs(value);
  if (date.isSame(reference, 'day')) {
    return date.format('h:mm A');
  }
  if (date.isSame(reference.subtract(1, 'day'), 'day')) {
    return 'Yesterday';
  }

  const dayDifference = reference
    .startOf('day')
    .diff(date.startOf('day'), 'day');
  if (dayDifference > 1 && dayDifference < 7) {
    return `${dayDifference} days ago`;
  }

  return date.format('MMM D, YYYY');
}

function getInterviewStart(interview: Interview): string {
  if (interview.confirmedStartsAt) return interview.confirmedStartsAt;
  if (interview.slots.length > 0) {
    return [...interview.slots].sort(
      (left, right) =>
        dayjs(left.startsAt).valueOf() - dayjs(right.startsAt).valueOf()
    )[0].startsAt;
  }
  return interview.createdAt;
}

function getInterviewMeta(interview: Interview): string {
  const meetingLabels: Record<Interview['meetingType'], string> = {
    CALL: 'Phone call',
    VIDEO: 'Video meeting',
    IN_PERSON: 'In person',
  };
  const status =
    interview.status === 'PROPOSED' || interview.status === 'RESCHEDULED'
      ? 'Awaiting confirmation'
      : formatStageLabel(interview.status);
  return `${meetingLabels[interview.meetingType]} · ${status}`;
}

export function buildDashboardActivities(
  interviews: Interview[],
  followUps: FeatureJobApplications[],
  reference: Dayjs = dayjs()
): DashboardActivity[] {
  const activities: DashboardActivity[] = [];
  const dayStart = reference.startOf('day').valueOf();

  followUps.forEach((application, index) => {
    activities.push({
      id: `follow-up-${application.id}`,
      applicationId: application.id,
      kind: 'follow-up',
      title: `Follow up with ${application.candidate || 'candidate'}`,
      meta: `${application.jobName || 'Job'} · ${formatStageLabel(
        application.status || 'WAITING_FOR_FEEDBACK'
      )}`,
      timeLabel: 'Open',
      sortValue: dayStart - followUps.length + index,
      statusLabel: 'Follow-up',
    });
  });

  interviews.forEach((interview) => {
    const startsAt = getInterviewStart(interview);
    const startsAtDate = dayjs(startsAt);
    if (
      !startsAtDate.isValid() ||
      !startsAtDate.isSame(reference, 'day') ||
      CANCELLED_INTERVIEW_STATUSES.has(interview.status)
    ) {
      return;
    }

    activities.push({
      id: `interview-${interview.id}`,
      applicationId: interview.jobApplicationId,
      kind: 'interview',
      title: interview.title || 'Candidate interview',
      meta: getInterviewMeta(interview),
      timeLabel: startsAtDate.format('h:mm A'),
      sortValue: startsAtDate.valueOf(),
      statusLabel: interview.status === 'CONFIRMED' ? 'Confirmed' : 'Interview',
    });
  });

  return activities.sort((left, right) => left.sortValue - right.sortValue);
}

export function countScheduledInterviews(interviews: Interview[]): number {
  return interviews.filter(
    (interview) => !CANCELLED_INTERVIEW_STATUSES.has(interview.status)
  ).length;
}

export function getPipelineTotal(stages: DashboardPipelineStage[]): number {
  return stages.reduce((total, stage) => total + stage.count, 0);
}
